import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, ResponsiveContainer } from "recharts";
import { useState, useEffect } from "react";

// Activity Rate Increase Component
const ActivityRateIncrease = ({ asyncFunction }) => {
  const [activityData, setActivityData] = useState(null);
  const [increaseRate, setIncreaseRate] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        const data = await asyncFunction();
        setActivityData(data);
        
        if (data && data.length >= 2) {
          const latest = data[data.length - 1];
          const first = data[0];
          
          const latestRate = latest.total > 0 ? (latest.active / latest.total) * 100 : 0;
          const firstRate = first.total > 0 ? (first.active / first.total) * 100 : 0;
          
          const increase = latestRate - firstRate;
          setIncreaseRate(increase);
        }
      })()
    }
  }, [asyncFunction]);

  if (!increaseRate) return null;

  const isPositive = increaseRate > 0;
  const isNegative = increaseRate < 0;
  const isNeutral = increaseRate === 0;

  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm text-gray-600">Activity Rate Change:</span>
      <span className={`text-sm font-semibold flex items-center gap-1 ${
        isPositive ? 'text-green-600' : 
        isNegative ? 'text-red-600' : 
        'text-gray-600'
      }`}>
        {isPositive && '↗️ '}
        {isNegative && '↘️ '}
        {isNeutral && '➡️ '}
        {isPositive ? '+' : ''}{increaseRate.toFixed(1)}%
      </span>
    </div>
  );
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-ktx-navy mb-2">{`Month: ${label}`}</p>
        <div className="space-y-1">
          <p className="text-sm">
            <span className="inline-block w-3 h-3 bg-ktx-purple rounded-full mr-2"></span>
            <span className="font-medium">Total Doctors:</span> 
            <span className="ml-2 text-ktx-purple font-bold">{data.total}</span>
          </p>
          <p className="text-sm">
            <span className="inline-block w-3 h-3 bg-sage-green rounded-full mr-2"></span>
            <span className="font-medium">Active Doctors:</span> 
            <span className="ml-2 text-sage-green font-bold">{data.active}</span>
          </p>
          <p className="text-sm pt-1 border-t border-gray-100">
            <span className="font-medium">Activity Rate:</span> 
            <span className="ml-2 text-ktx-navy font-bold">
              {data.total > 0 ? ((data.active / data.total) * 100).toFixed(1) : 0}%
            </span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export { ActivityRateIncrease };

export default function LineGraph({ asyncFunction }) {
  const [dynamicLineData, setDynamicLineData] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        setDynamicLineData(await asyncFunction());
      })()
    }
  }, [])

  return (
    <>
      {asyncFunction ? (
        <div className="w-full">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart 
              data={dynamicLineData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="name" 
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#783BFF" 
                strokeWidth={3}
                dot={{ fill: '#783BFF', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#783BFF', strokeWidth: 2 }}
                name="Total Doctors"
              />
              <Line 
                type="monotone" 
                dataKey="active" 
                stroke="#7FB1BA" 
                strokeWidth={3}
                dot={{ fill: '#7FB1BA', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#7FB1BA', strokeWidth: 2 }}
                name="Active Doctors"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="w-full h-[400px] flex justify-center items-center">
          <div className="w-20 h-20 border-4 border-dashed rounded-full animate-spin-slow border-ktx-purple flex justify-center items-center"></div>
        </div>
      )}
    </>
  )
}